/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { Grid, Box, Button, FormControl, OutlinedInput, InputLabel as MaterialInputLabel, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment-timezone';
import Currency from 'react-currency-formatter';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DateRangePicker, InputLabel, MultipleCheckboxesSelect, SearchIcon, ViewIcon } from 'components/elements';
import { useFetchInvoices, useFetchInvoiceStripe, useFetchPatientProviders } from 'hooks';
import { invoiceStatus, routes } from 'const';
import OutstandingBanner from './OutstandingBanner';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function MobileInvoiceView() {
  const classes = useStyles();
  const { t } = useTranslation(['labels', 'invoice']);
  const [status, setStatus] = useState(['unpaid']);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [providerId, setProviderId] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateRange, setDateRange] = useState({ key: 'selection' });
  const {
    data: invoices,
    totalDue,
    totalPaid,
    hasNextPage,
    fetchNextPage,
    isLoading,
    currency,
    grandDueAmount,
    total,
  } = useFetchInvoices({ status, invoiceNumber, providerId, startDate, endDate });
  const { getLink, download } = useFetchInvoiceStripe(invoiceId);
  const { providers } = useFetchPatientProviders();

  const handleDateRangeSelection = (ranges) => {
    setStartDate(ranges.selection.startDate);
    setEndDate(ranges.selection.endDate);
    setDateRange(ranges.selection);
  };

  const handleStatus = (event) => {
    setStatus(event);
  };

  const stripePay = (id) => {
    setInvoiceId(id);
    getLink(id);
  };

  const handleProviderChange = (event) => {
    setProviderId(event);
  };

  const handleBannerClick = () => {
    setStatus(['unpaid']);
    setInvoiceNumber(null);
    setStartDate(null);
    setEndDate(null);
    setProviderId([]);
    setDateRange({ key: 'selection' });
  };

  return (
    <Box sx={{ flexGrow: 1, paddingBottom: 7 }}>
      {grandDueAmount > 0 && <OutstandingBanner amount={<Currency quantity={grandDueAmount} currency={currency} />} onBannerClick={handleBannerClick} />}
      <Grid container spacing={2} className={classes.header}>
        <Grid item xs={12}>
          <DateRangePicker onChange={handleDateRangeSelection} dateRange={dateRange} />
        </Grid>
        <Grid item xs={12}>
          <MultipleCheckboxesSelect
            options={invoiceStatus.status.map((item) => {
              const inputStatus = { label: t(`${item === 'unpaid' ? 'patient_unpaid' : item}`), value: item };
              return inputStatus;
            })}
            defaultValue="Select"
            label={<InputLabel text={t('labels:select_a_status')} />}
            onChange={(event) => handleStatus(event)}
            value={status}
          />
        </Grid>
        <Grid item xs={12}>
          <MultipleCheckboxesSelect
            options={providers.map((item) => {
              const provider = { label: item.fullName, value: item.providerId };
              return provider;
            })}
            defaultValue="Select"
            label={<InputLabel text={t('labels:select_a_provider')} />}
            onChange={(event) => handleProviderChange(event)}
            value={providerId}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <MaterialInputLabel htmlFor="outlined-invoiceId">{t('labels:invoice_id')}</MaterialInputLabel>
            <OutlinedInput
              id="outlined-invoiceId"
              startAdornment={<SearchIcon />}
              label={t('labels:invoice_id')}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} mt={3}>
          {invoices.length ? (
            <>
              <InfiniteScroll
                dataLength={total}
                next={fetchNextPage}
                hasMore={hasNextPage}
                loader={<span className={classes.info}>{t('labels:loading')}</span>}
                scrollableTarget="scrollable"
              >
                <Box sx={{ flexGrow: 1 }}>
                  {invoices.map((row) => (
                    <Grid container mt={3} className={classes[`box${invoiceStatus.statusColor[row.attributes.status]}`]}>
                      <Grid item xs={4} className={classes.mobileLeftBox}>
                        <Typography component="p" variant="p" className={classes.amount}>
                          <Currency quantity={row.attributes.priceAmount} currency={row.attributes.priceCurrency} />
                        </Typography>
                        <Link to={`${routes.appointments}/${row.attributes.appointmentId}`}>[{moment(row.attributes.associatedAppointment.startDateTime).format('llll')}]</Link>
                      </Grid>
                      <Grid item xs={8} pl={1}>
                        <Typography component="p" variant="p">
                          <b>
                            {t('labels:invoice_id')} : {row.attributes.invoiceNumber}
                          </b>
                        </Typography>
                        <Typography component="p" variant="p" color="textSecondary">
                          <span className={classes.mobileHCS}>{row.attributes.healthcareServiceName}</span> {t('labels:with')} {row.attributes.requesterName}
                        </Typography>
                        {row.attributes.paidDate ? (
                          <Typography component="p" variant="p" mb={2} className={classes.textSuccess}>
                            {t('labels:invoice_paid')} {moment(row.attributes.paidDate).format('ll')}
                          </Typography>
                        ) : (
                          <Typography component="p" variant="p" mb={2} className={classes.textWarning}>
                            {t('labels:invoice_sent')} {moment(row.attributes.createdAt).format('ll')}
                          </Typography>
                        )}
                        <Box className={classes.boxAction}>
                          <Button variant="contained" href="#view-invoice" className={classes.viewInvoice} onClick={() => download(row.id)}>
                            <ViewIcon classes={classes.viewIcon} />
                          </Button>
                          {row.attributes.paymentMode === 'stripe' && row.attributes.status === 'unpaid' && (
                            <Button variant="contained" href="#contained-buttons" onClick={() => stripePay(row.id)} className={classes.payHere}>
                              {t('labels:pay_here')}
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </InfiniteScroll>
              <Grid item xs={12} mt={3} mb={4}>
                <Divider />
                <Box pt={2} pb={2} className={classes.boxInline}>
                  <Typography component="p" variant="p">
                    {t('labels:total_due')}:
                  </Typography>
                  <Typography component="p" variant="p">
                    &nbsp;
                    <Currency quantity={totalDue} currency={currency} />
                  </Typography>
                </Box>
                <Box className={classes.boxInline}>
                  <Typography component="p" variant="p">
                    {t('labels:total_paid')}:
                  </Typography>
                  <Typography component="p" variant="p">
                    &nbsp;
                    <Currency quantity={totalPaid} currency={currency} />
                  </Typography>
                </Box>
              </Grid>
            </>
          ) : (
            <div>
              <Typography variant="subtitle1" component="span">
                {isLoading ? t('labels:loading') : t('labels:no_invoices_available')}
              </Typography>
            </div>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default MobileInvoiceView;
